import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';

import DataTable, { FilterConfig } from '../../../component/datatable2';
import { MessageContext } from '../../../context/MessageContext';
import { LayoutContext } from '../../../context/LayoutContext';
import {
    ProductInventoryService,
    StockLevel,
    Warehouse,
    warehouseService,
} from '../../../service/warehouse';
import { StockService } from '../../../service/warehouse/stock/StockService';
import type { StockOperation } from '../../../service/warehouse/dto/StockOperation.dto';
import type { ResourceType } from '../../../service/warehouse/types';
import { RenderedHelper } from '../../../utils/render/RenderedHelper';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ReactComponent as WarehouseUserIcon } from '../../../assets/images/stock-levels/document-created-by.svg';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ReactComponent as WarehouseTransportIcon } from '../../../assets/images/stock-levels/warehouse-stock-transport.svg';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ReactComponent as WarehouseBagIcon } from '../../../assets/images/stock-levels/warehouse-stock-bag.svg';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ReactComponent as StockCheckIcon } from '../../../assets/images/stock-levels/button-stock-check.svg';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ReactComponent as TransferIcon } from '../../../assets/images/stock-levels/button-stock-transfer.svg';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ReactComponent as StockInIcon } from '../../../assets/images/add-circle-icon.svg';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ReactComponent as StockOutIcon } from '../../../assets/images/remove-circle-icon.svg';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ReactComponent as ExportIcon } from '../../../assets/images/stock-levels/button-export-documents.svg';

// @ts-ignore
import './WarehouseStockDetails.scss';


interface SelectOption {
    value: string;
    label: string;
}

interface WarehouseStockHistoryTableRow extends StockOperation {
    stock_after: string;
    route_id: string;
    location_or_device: string;
    created_by_display: string;
}

type WarehouseStockAction =
    | 'STOCK_CHECK'
    | 'STOCK_IN'
    | 'STOCK_OUT'
    | 'TRANSFER';

const getProductOptionId = (option: any): string =>
    String(option?.id ?? option?.value ?? option?.externalId ?? option?.external_id ?? '');

const getProductOptionLabel = (option: any): string =>
    String(
        option?.label ??
        option?.name ??
        option?.productName ??
        option?.product_name ??
        getProductOptionId(option),
    );

const wrapCell = (value?: React.ReactNode) => (
    <div className="warehouse-stock-details__table-cell">
        {value ?? ''}
    </div>
);

const STOCK_LEVELS_PATH = '/products/stock-levels';

const WarehouseStockDetails: React.FC = () => {
    const { warehouseId } = useParams<{ warehouseId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { showErrorMessage } = useContext(MessageContext) as any;

    const {
        setShowBackButton,
        setBackButtonLabel,
        setBackButtonNavigateTo,
        setBackButtonHandleOnClick,
        setShowPageTitle,
    } = useContext(LayoutContext) as any;

    const productInventoryService = useMemo(() => new ProductInventoryService(), []);
    const stockService = useMemo(() => new StockService(), []);

    const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
    const [productOptions, setProductOptions] = useState<SelectOption[]>([]);
    const [stockByProductId, setStockByProductId] = useState<Record<string, StockLevel>>({});
    const [productNameById, setProductNameById] = useState<Record<string, string>>({});
    const [productsLoading, setProductsLoading] = useState(false);
    const [totalStockItems, setTotalStockItems] = useState(0);
    const [selectedStockLevel, setSelectedStockLevel] = useState<string>('---');

    const handleBackClick = useCallback(() => {
        navigate(STOCK_LEVELS_PATH);
        return false;
    }, [navigate]);

    const renderBreadcrumbLabel = useCallback(
        () => (
            <>
                <span
                    onClick={() => navigate('/products')}
                    style={{ cursor: 'pointer' }}
                >
                    {t('menu.products', 'Products')}
                </span>

                <span> / </span>

                <span
                    onClick={() => navigate(STOCK_LEVELS_PATH)}
                    style={{ cursor: 'pointer' }}
                >
                    {t(
                        'products.tabs.stockLevels',
                        'Stock levels',
                    )}
                </span>

                <span> / </span>

                <span>
                    {warehouse?.name ??
                        t(
                            'stockLevels.warehouseStock.title',
                            'Warehouse Stock Details',
                        )}
                </span>
            </>
        ),
        [
            navigate,
            t,
            warehouse?.name,
        ],
    );

    useEffect(() => {
        setShowBackButton(true);
        setBackButtonLabel(renderBreadcrumbLabel());
        setBackButtonNavigateTo(STOCK_LEVELS_PATH);
        setBackButtonHandleOnClick(() => handleBackClick);
        setShowPageTitle(false);

        return () => {
            setShowBackButton(false);
            setBackButtonHandleOnClick(null);
            setShowPageTitle(true);
        };
    }, [
        handleBackClick,
        renderBreadcrumbLabel,
        setShowBackButton,
        setBackButtonLabel,
        setBackButtonNavigateTo,
        setBackButtonHandleOnClick,
        setShowPageTitle,
    ]);

    useEffect(() => {
        if (!warehouseId) return;

        let mounted = true;

        const loadWarehouse = async () => {
            try {
                const loadedWarehouse = await warehouseService.get(warehouseId);
                if (!mounted) return;
                setWarehouse(loadedWarehouse);
            } catch {
                if (!mounted) return;
                showErrorMessage(
                    t('stockLevels.error.fetchWarehouse', 'Failed to load warehouse'),
                );
            }
        };

        loadWarehouse();

        return () => {
            mounted = false;
        };
    }, [warehouseId, showErrorMessage, t]);

    useEffect(() => {
        if (!warehouseId) return;

        let mounted = true;

        const loadWarehouseProducts = async () => {
            setProductsLoading(true);

            try {
                const [products, stockResponse] = await Promise.all([
                    productInventoryService.getAllProducts(),
                    warehouseService.getStock(warehouseId, {
                        page: 1,
                        size: 1000,
                    } as any),
                ]);

                if (!mounted) return;

                const nameById = (products as any[]).reduce<Record<string, string>>(
                    (acc, product) => {
                        const id = getProductOptionId(product);
                        const label = getProductOptionLabel(product);

                        if (id) acc[id] = label;

                        return acc;
                    },
                    {},
                );

                const stockMap = stockResponse.content.reduce<Record<string, StockLevel>>(
                    (acc, item) => {
                        acc[item.resource_id] = item;
                        return acc;
                    },
                    {},
                );

                const options = stockResponse.content.map(item => ({
                    value: item.resource_id,
                    label:
                        nameById[item.resource_id] ??
                        item.resource_name ??
                        item.resource_id,
                }));

                setProductNameById(nameById);
                setStockByProductId(stockMap);
                setProductOptions(options);
                setTotalStockItems(stockResponse.total_elements);

                setSelectedProduct(null);
                setSelectedStockLevel('---');
            } catch {
                if (!mounted) return;

                showErrorMessage(
                    t(
                        'stockLevels.error.fetchWarehouseStock',
                        'Failed to load warehouse stock',
                    ),
                );
            } finally {
                if (mounted) setProductsLoading(false);
            }
        };

        loadWarehouseProducts();

        return () => {
            mounted = false;
        };
    }, [warehouseId, productInventoryService, showErrorMessage, t]);

    const selectedProductOption = useMemo(
        () => productOptions.find(product => product.value === selectedProduct) ?? null,
        [productOptions, selectedProduct],
    );

    const selectedStockItem = useMemo(
        () => (selectedProduct ? stockByProductId[selectedProduct] : undefined),
        [selectedProduct, stockByProductId],
    );

    const buildSelectedStockDocumentSetup = useCallback(() => {
        if (!warehouseId || !selectedProduct || !selectedStockItem) {
            return null;
        }

        const productName =
            productNameById[selectedProduct] ??
            selectedStockItem.resource_name ??
            selectedProductOption?.label ??
            selectedProduct;

        const productType = selectedStockItem.resource_type as ResourceType;

        return {
            warehouseId,
            targetWarehouseId: warehouseId,
            productType,
            selectedItems: [
                {
                    id: selectedStockItem.resource_id,
                    productId: selectedStockItem.resource_id,
                    productType,
                    productName,
                    quantity: Number(selectedStockItem.quantity ?? 0),
                    warehouseId,
                },
            ],
            selectedResourceIds: [selectedStockItem.resource_id],
        };
    }, [
        warehouseId,
        selectedProduct,
        selectedStockItem,
        selectedProductOption,
        productNameById,
    ]);

    const handleToolbarAction = useCallback(
        (action: WarehouseStockAction) => {
            const stockDocumentSetup = buildSelectedStockDocumentSetup();

            if (!stockDocumentSetup) {
                showErrorMessage(
                    t('stockLevels.error.selectProductFirst', 'Select product first'),
                );
                return;
            }

            if (action === 'STOCK_CHECK') {
                navigate('/products/stock-documents/stock-check', {
                    state: {
                        stockCheckSetup: {
                            warehouseId,
                            productType: stockDocumentSetup.productType,
                            includeAllProductsFromWarehouse: false,
                            fillWithCurrentStock: true,
                            selectedItems: stockDocumentSetup.selectedItems,
                            selectedResourceIds: stockDocumentSetup.selectedResourceIds,
                        },
                    },
                });
                return;
            }

            if (action === 'STOCK_IN') {
                navigate('/products/stock-documents/stock-in', {
                    state: {
                        stockDocumentSetup: {
                            ...stockDocumentSetup,
                            warehouseId,
                            targetWarehouseId: warehouseId,
                        },
                    },
                });
                return;
            }

            if (action === 'STOCK_OUT') {
                navigate('/products/stock-documents/stock-out', {
                    state: {
                        stockDocumentSetup: {
                            ...stockDocumentSetup,
                            warehouseId,
                            targetWarehouseId: null,
                        },
                    },
                });
                return;
            }

            if (action === 'TRANSFER') {
                navigate('/products/stock-documents/stock-transfer', {
                    state: {
                        stockDocumentSetup: {
                            ...stockDocumentSetup,
                            warehouseId,
                            targetWarehouseId: null,
                        },
                    },
                });
            }
        },
        [
            buildSelectedStockDocumentSetup,
            navigate,
            warehouseId,
            showErrorMessage,
            t,
        ],
    );

    const filterConfig: FilterConfig[] = useMemo(
        () => [
            {
                fieldName: 'created_at',
                label: t('stockLevels.columns.dateTime', 'DATE & TIME'),
                type: 'TEXT',
                apiParamName: 'created_at',
            },
            {
                fieldName: 'route_id',
                label: t('stockLevels.columns.routeId', 'ROUTE ID'),
                type: 'TEXT',
                apiParamName: 'route_id',
            },
            {
                fieldName: 'resource_name',
                label: t('stockLevels.columns.product', 'PRODUCT'),
                type: 'TEXT',
                apiParamName: 'resource_name',
            },
            {
                fieldName: 'operation_type',
                label: t('stockLevels.columns.operationType', 'OPERATION TYPE'),
                type: 'TEXT',
                apiParamName: 'operation_type',
            },
            {
                fieldName: 'location_or_device',
                label: t('stockLevels.columns.locationOrDevice', 'LOCATION OR DEVICE'),
                type: 'TEXT',
                apiParamName: 'location_or_device',
            },
            {
                fieldName: 'created_by_display',
                label: t('stockLevels.columns.createdBy', 'CREATED BY'),
                type: 'TEXT',
                apiParamName: 'created_by_display',
            },
            {
                fieldName: 'quantity',
                label: t('stockLevels.columns.quantity', 'QUANTITY'),
                type: 'NUMBER_RANGE',
                apiParamName: 'quantity',
                params: {
                    minValue: 0,
                    maxValue: 999999,
                },
            },
            {
                fieldName: 'stock_after',
                label: t('stockLevels.columns.stockAfter', 'STOCK AFTER'),
                type: 'NUMBER_RANGE',
                apiParamName: 'stock_after',
                params: {
                    minValue: 0,
                    maxValue: 999999,
                },
            },
        ],
        [t],
    );

    const translateOperationType = useCallback(
        (operationType?: string) => {
            if (!operationType) return '';

            return t(
                `stockLevels.operationType.${operationType}`,
                operationType,
            );
        },
        [t],
    );

    const getSignedQuantityDisplay = (
        value: string | number | undefined,
        operationType?: string,
    ) => {
        const quantity = Number(value ?? 0);

        if (operationType === 'DECREASE') {
            return `-${quantity}`;
        }

        return String(quantity);
    };

    const fetchData = useCallback(
        async (criteria: any) => {
            if (!warehouseId) {
                return {
                    data: [],
                    totalRecords: 0,
                };
            }

            try {
                const response = await stockService.getHistory({
                    page:
                        criteria.first != null && criteria.rows
                            ? Math.floor(criteria.first / criteria.rows) + 1
                            : 1,
                    size: criteria.rows ?? 25,
                    sort: criteria.sortField
                        ? `${criteria.sortField}:${criteria.sortOrder === -1 ? 'desc' : 'asc'}`
                        : 'created_at:desc',
                    warehouse_id: warehouseId,
                    resource_type: 'PRODUCT',
                    resource_id: selectedProduct ?? undefined,
                });

                const runningStockByResourceId: Record<string, number> = {};

                const data: WarehouseStockHistoryTableRow[] = response.content.map(item => {
                    const resourceId = item.resource_id;

                    if (runningStockByResourceId[resourceId] === undefined) {
                        runningStockByResourceId[resourceId] = Number(
                            stockByProductId[resourceId]?.quantity ?? 0,
                        );
                    }

                    const stockAfter = runningStockByResourceId[resourceId];
                    const quantity = Number(item.quantity ?? 0);

                    if (item.operation_type === 'INCREASE') {
                        runningStockByResourceId[resourceId] -= quantity;
                    }

                    if (item.operation_type === 'DECREASE') {
                        runningStockByResourceId[resourceId] += quantity;
                    }

                    return {
                        ...item,
                        resource_name:
                            productNameById[item.resource_id] ??
                            item.resource_name ??
                            item.resource_id,
                        stock_after: String(stockAfter),
                        route_id: '',
                        location_or_device: '',
                        created_by_display: '',
                    };
                });

                const selectedQuantity = selectedProduct
                    ? stockByProductId[selectedProduct]?.quantity
                    : undefined;

                setSelectedStockLevel(
                    selectedQuantity != null ? String(selectedQuantity) : '---',
                );

                return {
                    data,
                    totalRecords: response.total_elements,
                };
            } catch {
                showErrorMessage(
                    t(
                        'stockLevels.error.fetchWarehouseStock',
                        'Failed to load warehouse stock',
                    ),
                );

                return {
                    data: [],
                    totalRecords: 0,
                };
            }
        },
        [
            warehouseId,
            selectedProduct,
            stockByProductId,
            productNameById,
            stockService,
            showErrorMessage,
            t,
        ],
    );

    const tableToolbarRight = useMemo(
        () => (
            <div className="warehouse-stock-details__buttons">
                <Button className="small">
                    <ExportIcon className="icon" />
                    <span className="p-button-label pl-2">
                        {t('common.exportData', 'Export data')}
                    </span>
                </Button>

                <Button className="small" onClick={() => handleToolbarAction('STOCK_CHECK')}>
                    <StockCheckIcon className="icon" />
                    <span className="p-button-label pl-2">
                        {t('stockLevels.actions.STOCK_CHECK', 'Stock Check')}
                    </span>
                </Button>

                <Button className="small" onClick={() => handleToolbarAction('TRANSFER')}>
                    <TransferIcon className="icon" />
                    <span className="p-button-label pl-2">
                        {t('stockLevels.actions.TRANSFER', 'Stock Transfer')}
                    </span>
                </Button>

                <Button className="small" onClick={() => handleToolbarAction('STOCK_IN')}>
                    <StockInIcon className="icon" />
                    <span className="p-button-label pl-2">
                        {t('stockLevels.actions.STOCK_IN', 'Stock In')}
                    </span>
                </Button>

                <Button className="small p-button-danger" onClick={() => handleToolbarAction('STOCK_OUT')}>
                    <StockOutIcon className="icon" />
                    <span className="p-button-label pl-2">
                        {t('stockLevels.actions.STOCK_OUT', 'Stock Out')}
                    </span>
                </Button>
            </div>
        ),
        [t, handleToolbarAction],
    );

    const isMobileWarehouse = warehouse?.type === 'MOBILE';

    return (
        <div className="warehouse-stock-details">
            <div className="warehouse-stock-details__summary">
                <div className="warehouse-stock-details__summary-left">
                    <div className="warehouse-stock-details__summary-item">
                        <WarehouseUserIcon className="warehouse-stock-details__summary-icon" />

                        <span>
                            {warehouse?.address_city || warehouse?.address_street
                                ? [
                                    warehouse?.address_city,
                                    warehouse?.address_street,
                                ]
                                    .filter(Boolean)
                                    .join(', ')
                                : warehouse?.name ?? 'Warehouse'}
                        </span>
                    </div>

                    <div className="warehouse-stock-details__summary-item">
                        <WarehouseTransportIcon className="warehouse-stock-details__summary-icon" />
                        <span>{'--'}</span>
                    </div>

                    <div className="warehouse-stock-details__summary-item">
                        <WarehouseBagIcon className="warehouse-stock-details__summary-icon" />

                        <span>
                            {t(
                                'stockLevels.warehouseStock.numberOfProducts',
                                'NUMBER OF PRODUCTS UNITS',
                            )}

                            <strong>{totalStockItems}</strong>
                        </span>
                    </div>

                    <div className="warehouse-stock-details__summary-item">
                        <span>
                            {t(
                                'stockLevels.warehouseStock.mobileWarehouse',
                                'Mobile Warehouse:',
                            )}
                        </span>

                        <span
                            className={
                                isMobileWarehouse
                                    ? 'warehouse-stock-details__pill warehouse-stock-details__pill--success'
                                    : 'warehouse-stock-details__pill warehouse-stock-details__pill--danger'
                            }
                        >
                            {isMobileWarehouse
                                ? t('Boolean.true', 'Yes')
                                : t('Boolean.false', 'No')}
                        </span>
                    </div>
                </div>

                <div className="warehouse-stock-details__summary-right">
                    <div className="warehouse-stock-details__summary-separator" />

                    <Dropdown
                        value={selectedProduct}
                        options={productOptions}
                        optionLabel="label"
                        optionValue="value"
                        onChange={e => setSelectedProduct(e.value ?? null)}
                        placeholder={t(
                            'stockLevels.warehouseStock.selectProduct',
                            'Select Product',
                        )}
                        className="warehouse-stock-details__product-select"
                        loading={productsLoading}
                        showClear
                        filter
                    />

                    <div className="warehouse-stock-details__stock-level">
                        <span>
                            {t('stockLevels.columns.stockLevel', 'STOCK LEVEL:')}
                        </span>

                        <strong>{selectedStockLevel}</strong>
                    </div>
                </div>
            </div>

            <div className="warehouse-stock-details__table-card">
                <DataTable
                    key={selectedProduct ?? 'all-products'}
                    fetchData={fetchData}
                    filterConfig={filterConfig}
                    globalSearchPosition="left"
                    tableToolbarRight={tableToolbarRight}
                    minWidth="1180px"
                >
                    <Column
                        key="created_at"
                        field="created_at"
                        header={t('stockLevels.columns.dateTime', 'DATE & TIME')}
                        sortable
                        sortField="created_at"
                        style={{ width: '125px', minWidth: '125px', maxWidth: '125px' }}
                        body={(row: WarehouseStockHistoryTableRow) =>
                            wrapCell(
                                RenderedHelper.formatTimestampNoSeconds(
                                    row.created_at,
                                ),
                            )
                        }
                    />

                    <Column
                        key="route_id"
                        field="route_id"
                        header={t('stockLevels.columns.routeId', 'ROUTE ID')}
                        style={{ width: '210px', minWidth: '210px', maxWidth: '210px' }}
                        body={(row: WarehouseStockHistoryTableRow) => wrapCell(row.route_id)}
                    />

                    <Column
                        key="resource_name"
                        field="resource_name"
                        header={t('stockLevels.columns.product', 'PRODUCT')}
                        sortable
                        sortField="resource_name"
                        style={{ width: '230px', minWidth: '230px', maxWidth: '230px' }}
                        headerClassName="dt2-header-wrap"
                        body={(row: WarehouseStockHistoryTableRow) => wrapCell(row.resource_name)}
                    />

                    <Column
                        key="operation_type"
                        field="operation_type"
                        header={t(
                            'stockLevels.columns.operationType',
                            'OPERATION TYPE',
                        )}
                        sortable
                        sortField="operation_type"
                        style={{ width: '150px', minWidth: '150px', maxWidth: '150px' }}
                        body={(row: WarehouseStockHistoryTableRow) =>
                            wrapCell(translateOperationType(row.operation_type))
                        }
                    />

                    <Column
                        key="location_or_device"
                        field="location_or_device"
                        header={t(
                            'stockLevels.columns.locationOrDevice',
                            'LOCATION OR DEVICE',
                        )}
                        style={{ width: '220px', minWidth: '220px', maxWidth: '220px' }}
                        headerClassName="dt2-header-wrap"
                        body={(row: WarehouseStockHistoryTableRow) =>
                            wrapCell(row.location_or_device)
                        }
                    />

                    <Column
                        key="created_by_display"
                        field="created_by_display"
                        header={t('stockLevels.columns.createdBy', 'CREATED BY')}
                        style={{ width: '150px', minWidth: '150px', maxWidth: '150px' }}
                        headerClassName="dt2-header-wrap"
                        body={(row: WarehouseStockHistoryTableRow) =>
                            wrapCell(row.created_by_display)
                        }
                    />

                    <Column
                        key="quantity"
                        field="quantity"
                        header={t('stockLevels.columns.quantity', 'QUANTITY')}
                        sortable
                        sortField="quantity"
                        style={{ width: '110px', minWidth: '110px', maxWidth: '110px' }}
                        body={(row: WarehouseStockHistoryTableRow) => (
                            <div
                                className={
                                    row.operation_type === 'DECREASE'
                                        ? 'warehouse-stock-details__table-cell warehouse-stock-details__table-cell--negative'
                                        : 'warehouse-stock-details__table-cell'
                                }
                            >
                                {getSignedQuantityDisplay(row.quantity, row.operation_type)}
                            </div>
                        )}
                    />

                    <Column
                        key="stock_after"
                        field="stock_after"
                        header={t('stockLevels.columns.stockAfter', 'STOCK AFTER')}
                        style={{ width: '130px', minWidth: '130px', maxWidth: '130px' }}
                        headerClassName="dt2-header-wrap"
                        body={(row: WarehouseStockHistoryTableRow) => (
                            <div
                                className={
                                    Number(row.stock_after) < 0
                                        ? 'warehouse-stock-details__table-cell warehouse-stock-details__table-cell--negative'
                                        : 'warehouse-stock-details__table-cell'
                                }
                            >
                                {row.stock_after}
                            </div>
                        )}
                    />
                </DataTable>
            </div>
        </div>
    );
};

export default WarehouseStockDetails;